import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Loader2, Check, X, MessagesSquare } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck } from "@/components/common/TrustBadge";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// A Circle is a small group chat. Backend accepts 2–19 member_ids at creation (total cap 20 — add more later).
export const CIRCLE_CREATE_MAX = 19;
export const CIRCLE_MEMBER_CAP = 20;

export function NewCircleDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]); // array of user objects

  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts"],
    queryFn: api.listContacts,
    enabled: open,
  });
  const { data: searchResults = [], isFetching } = useQuery({
    queryKey: ["user-search", q],
    queryFn: () => api.searchUsers(q),
    enabled: open && q.trim().length >= 1,
  });

  const people = q.trim().length >= 1 ? searchResults : contacts;
  const selectedIds = new Set(selected.map((u) => u.id));

  const toggle = (u) => {
    if (selectedIds.has(u.id)) {
      setSelected((s) => s.filter((x) => x.id !== u.id));
    } else if (selected.length >= CIRCLE_CREATE_MAX) {
      toast.error(`You can add up to ${CIRCLE_CREATE_MAX} people when creating a Circle.`);
    } else {
      setSelected((s) => [...s, u]);
    }
  };

  const reset = () => { setQ(""); setName(""); setSelected([]); };

  const createMut = useMutation({
    mutationFn: () => api.createCircle(name.trim() || null, selected.map((u) => u.id)),
    onSuccess: (chat) => {
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.invalidateQueries({ queryKey: ["circles-list"] });
      qc.invalidateQueries({ queryKey: ["my-limits"] });
      onOpenChange(false);
      reset();
      const id = chat?.id || chat?.conversation_id;
      toast.success("Circle created 🎉");
      if (id) navigate(`/app/chats/${id}`);
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      if (d?.code === "creation_limit" || d?.error === "creation_limit") {
        toast.error(d.message || "You've reached your Circle limit — raise your Trust Score to create more.");
      } else {
        toast.error((typeof d === "string" && d) || d?.message || "Couldn't create the Circle.");
      }
    },
  });

  const canCreate = selected.length >= 2 && selected.length <= CIRCLE_CREATE_MAX && !createMut.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="new-circle-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <MessagesSquare className="h-5 w-5 text-bloq-mint" /> New Circle
          </DialogTitle>
        </DialogHeader>

        <input
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 40))}
          placeholder="Circle name (optional)"
          className="h-11 w-full rounded-xl border border-white/10 bg-bloq-bg px-3 text-sm outline-none focus:border-bloq-mint/40"
          data-testid="circle-name-input"
        />

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="circle-selected-chips">
            {selected.map((u) => (
              <span key={u.id} className="flex items-center gap-1 rounded-full bg-bloq-mint/15 py-1 pl-1 pr-2 text-xs font-semibold text-bloq-mint" data-testid={`circle-chip-${u.username}`}>
                <Avatar user={u} size="xs" />
                {u.display_name?.split(" ")[0] || u.username}
                <button onClick={() => toggle(u)} className="ml-0.5 rounded-full hover:bg-white/10" data-testid={`circle-chip-remove-${u.username}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-bloq-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people to add"
            className="h-11 w-full rounded-xl border border-white/10 bg-bloq-bg pl-9 pr-3 text-sm outline-none focus:border-bloq-mint/40"
            data-testid="circle-member-search"
          />
        </div>

        <div className="max-h-64 space-y-1 overflow-y-auto">
          {isFetching && (
            <div className="flex justify-center py-6 text-bloq-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
          )}
          {!isFetching && people.length === 0 && (
            <p className="py-6 text-center text-sm text-bloq-muted">
              {q.trim().length >= 1 ? "No people found." : "No connections yet — search to add people."}
            </p>
          )}
          {people.map((u) => {
            const picked = selectedIds.has(u.id);
            return (
              <button
                key={u.id}
                onClick={() => toggle(u)}
                className={cn("flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors hover:bg-white/5", picked && "bg-bloq-mint/[0.06]")}
                data-testid={`circle-person-${u.username}`}
              >
                <Avatar user={u} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-semibold">{u.display_name}</span>
                    {u.verified && <VerifiedCheck size={12} />}
                  </div>
                  <div className="truncate text-xs text-bloq-muted">@{u.username}</div>
                </div>
                <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-full border", picked ? "border-bloq-mint bg-bloq-mint text-bloq-bg" : "border-white/20")}>
                  {picked && <Check className="h-3.5 w-3.5" />}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-white/8 pt-3">
          <span className="text-xs text-bloq-muted" data-testid="circle-select-hint">
            {selected.length < 2
              ? "Pick at least 2 people"
              : `${selected.length} selected · up to ${CIRCLE_MEMBER_CAP} total`}
          </span>
          <button
            onClick={() => createMut.mutate()}
            disabled={!canCreate}
            className="flex items-center gap-2 rounded-full bloq-gradient px-5 py-2 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-40"
            data-testid="circle-create-btn"
          >
            {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessagesSquare className="h-4 w-4" />}
            Create Circle
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
